import type { AxiosRequestConfig } from "axios";
import useSWR, { type SWRConfiguration, type SWRResponse } from "swr";
import useSWRMutation, {
  type SWRMutationConfiguration,
  type SWRMutationResponse,
} from "swr/mutation";
import type { PaginatedResponse } from "../types";
import { apiClient } from "./client";

/**
 * Generic GET hook using SWR
 */
export function useGet<T>(
  endpoint: string | null,
  config?: AxiosRequestConfig,
  swrConfig?: SWRConfiguration<T>,
): SWRResponse<T> {
  return useSWR(
    endpoint ? [endpoint, config] : null,
    ([url, axiosConfig]) => apiClient.get<T>(url, axiosConfig),
    swrConfig,
  );
}

/**
 * Generic paginated GET hook using SWR
 */
export function usePaginatedGet<T>(
  endpoint: string | null,
  params?: Record<string, unknown>,
  config?: AxiosRequestConfig,
  swrConfig?: SWRConfiguration<PaginatedResponse<T>>,
): SWRResponse<PaginatedResponse<T>> {
  return useSWR(
    endpoint ? [endpoint, params, config] : null,
    ([url, queryParams, axiosConfig]) => apiClient.getPaginated<T>(url, queryParams, axiosConfig),
    swrConfig,
  );
}

/**
 * Generic POST mutation hook using SWR
 */
export function usePost<T, D = unknown>(
  endpoint: string,
  config?: AxiosRequestConfig,
  swrConfig?: SWRMutationConfiguration<T, Error, string, D>,
): SWRMutationResponse<T, Error, string, D> {
  return useSWRMutation(
    endpoint,
    (url, { arg }: { arg: D }) => apiClient.post<T, D>(url, arg, config),
    swrConfig,
  );
}

/**
 * Generic PUT mutation hook using SWR
 */
export function usePut<T, D = unknown>(
  endpoint: string,
  config?: AxiosRequestConfig,
  swrConfig?: SWRMutationConfiguration<T, Error, string, D>,
): SWRMutationResponse<T, Error, string, D> {
  return useSWRMutation(
    endpoint,
    (url, { arg }: { arg: D }) => apiClient.put<T, D>(url, arg, config),
    swrConfig,
  );
}

/**
 * Generic PATCH mutation hook using SWR
 */
export function usePatch<T, D = unknown>(
  endpoint: string,
  config?: AxiosRequestConfig,
  swrConfig?: SWRMutationConfiguration<T, Error, string, D>,
): SWRMutationResponse<T, Error, string, D> {
  return useSWRMutation(
    endpoint,
    (url, { arg }: { arg: D }) => apiClient.patch<T, D>(url, arg, config),
    swrConfig,
  );
}

/**
 * Generic DELETE mutation hook using SWR
 */
export function useDelete<T>(
  endpoint: string,
  config?: AxiosRequestConfig,
  swrConfig?: SWRMutationConfiguration<T, Error, string, void>,
): SWRMutationResponse<T, Error, string, void> {
  return useSWRMutation(endpoint, (url) => apiClient.delete<T>(url, config), swrConfig);
}

/**
 * Hook for conditional GET requests (useful for dependent queries)
 */
export function useConditionalGet<T>(
  endpoint: string | null,
  condition: boolean,
  config?: AxiosRequestConfig,
  swrConfig?: SWRConfiguration<T>,
): SWRResponse<T> {
  return useGet<T>(condition && endpoint ? endpoint : null, config, swrConfig);
}

/**
 * Hook for GET requests with automatic retry on error
 */
export function useGetWithRetry<T>(
  endpoint: string | null,
  config?: AxiosRequestConfig,
  retryCount = 3,
  retryInterval = 1000,
): SWRResponse<T> {
  return useGet<T>(endpoint, config, {
    errorRetryCount: retryCount,
    errorRetryInterval: retryInterval,
    shouldRetryOnError: (error) => {
      // Only retry on network errors or 5xx server errors
      return !error.response || error.response.status >= 500;
    },
  });
}

/**
 * Hook for GET requests with polling
 */
export function useGetWithPolling<T>(
  endpoint: string | null,
  interval: number,
  config?: AxiosRequestConfig,
  swrConfig?: SWRConfiguration<T>,
): SWRResponse<T> {
  return useGet<T>(endpoint, config, {
    refreshInterval: interval,
    ...swrConfig,
  });
}
